import cx from 'classnames';
import { useTranslation } from 'next-i18next';
import * as React from 'react';
import { get, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { FormError } from './form.error';
import { FormHelpMessage } from './form.help-message';
import { FormLabel } from './form.label';
import type { FormInputBase } from './form.types';
import { Text } from '@dx-ui/global-ui-prism';

/**
 * This component is to be used in place of all `<input>` elements including checkbox and radio
 */
export const FormInput: React.FC<
  React.PropsWithChildren<FormInputBase<React.InputHTMLAttributes<HTMLInputElement>>> & {
    enableInlineError?: boolean;
  }
> = ({
  label,
  name,
  required,
  optional,
  registerOptions,
  labelClassName,
  helpMessage,
  loading,
  className,
  containerClassName,
  enableInlineError = true,
  type = 'text',
  ...rest
}) => {
  'use no memo';
  const id = React.useId();
  const errorId = `input-error-${id}`;
  const helperId = `input-helper-${id}`;
  const [t] = useTranslation('osc-form');
  const { register } = useFormContext();
  const { errors } = useFormState({ name });
  const fieldError = get(errors, name);
  const hasError = !!fieldError;
  const isDateInput = type === 'date';
  const hasDatePlaceholder = isDateInput && !!rest.placeholder;
  const dateValue = useWatch({ name, disabled: !hasDatePlaceholder });
  const dateWorkaroundAriaLabel =
    hasDatePlaceholder && typeof label === 'string' ? label : rest['aria-label'];

  let inputClassName = '';
  let labelClass = '';
  switch (type) {
    case 'checkbox':
      inputClassName = 'form-checkbox';
      labelClass = 'label-inline-reverse';
      break;
    case 'radio':
      inputClassName = 'form-radio';
      labelClass = 'label-inline';
      break;
    default:
      inputClassName = 'form-input w-full';
      break;
  }

  const inputEl = (
    <input
      type={type}
      {...rest}
      data-dd-privacy="mask-user-input"
      className={cx(inputClassName, className, {
        'form-error': hasError,
        'text-transparent': hasDatePlaceholder && !dateValue,
      })}
      aria-invalid={hasError}
      required={required}
      aria-label={dateWorkaroundAriaLabel}
      aria-describedby={`${errorId} ${helperId} ${rest?.['aria-describedby'] || ''}`}
      {...register(name, {
        required: required ? t('requiredError') : false,
        ...registerOptions,
      })}
    />
  );

  return (
    <div className={containerClassName}>
      <FormLabel
        label={label}
        required={required}
        optional={optional}
        hasError={hasError}
        type={type}
        className={cx('self-start', labelClass, labelClassName)}
      >
        {hasDatePlaceholder ? (
          <span className="relative block">
            {inputEl}
            {!dateValue && (
              <Text
                as="span"
                color="muted"
                className="pointer-events-none absolute inset-y-0 start-3 flex items-center"
                aria-hidden="true"
              >
                {rest.placeholder}
              </Text>
            )}
          </span>
        ) : (
          inputEl
        )}
      </FormLabel>
      <FormHelpMessage id={helperId} message={helpMessage} isLoading={loading} />
      {enableInlineError ? <FormError id={errorId} error={fieldError} /> : null}
    </div>
  );
};

export default FormInput;
